'use client'

import { ChevronRight } from 'lucide-react'
import { Reveal } from '../hooks/useReveal'

const capabilities = [
  {
    index: '01',
    title: 'Real-time vision',
    body: 'Reads context as it happens and surfaces what matters before you ask.',
  },
  {
    index: '02',
    title: 'Layered insight',
    body: 'Moves from rough outline to sharp output without losing the thread.',
  },
  {
    index: '03',
    title: 'Adaptive speed',
    body: 'Learns your cadence and tightens every pass as you work.',
  },
]

export function Capability() {
  return (
    <section
      className="relative min-h-screen min-h-[100svh] flex flex-col justify-between"
      style={{ paddingTop: '6rem', paddingBottom: '3rem' }}
    >
      <div className="px-5 sm:px-8 md:px-12 w-full">
        {/* Top row: badge left, copy right */}
        <div className="flex flex-col sm:flex-row justify-between gap-8 mb-12 sm:mb-0">
          {/* Left badge */}
          <Reveal delay={120}>
            <span className="border-l-2 border-white bg-white/15 px-3 py-1.5 backdrop-blur-md font-mono text-[11px] uppercase tracking-[0.15em] inline-block">
              Insight On Demand
            </span>
          </Reveal>

          {/* Right copy */}
          <Reveal delay={220} className="max-w-sm sm:text-right">
            <p className="text-lg sm:text-xl leading-relaxed text-white drop-shadow-md">
              Our AI doesn&apos;t just respond — it interprets, sharpens, and delivers the signal you need.
            </p>
          </Reveal>
        </div>

        {/* Bottom area */}
        <div className="flex flex-col md:flex-row items-end justify-between gap-12 md:gap-16 flex-1">
          {/* Left column: H2 + body + CTAs */}
          <div className="max-w-xl w-full">
            <Reveal delay={180}>
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-normal leading-[1.05] tracking-tight text-white drop-shadow-lg">
                Learn to see<br />
                brilliantly.
              </h2>
            </Reveal>

            <Reveal delay={320} className="mt-6 max-w-md text-sm sm:text-base text-white/80 drop-shadow-md">
              <p>
                From the first sketch to the final render, Nova turns raw intent into decisions your team can act on — quietly, precisely, at speed.
              </p>
            </Reveal>

            <Reveal delay={420} className="mt-8 flex flex-wrap gap-3">
              <button className="rounded-full bg-white px-5 py-2.5 text-xs sm:text-sm font-medium text-black hover:bg-white/85 transition-colors duration-300 flex items-center gap-1.5">
                Run the demo
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button className="rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-5 py-2.5 text-xs sm:text-sm text-white hover:bg-white/20 transition-colors duration-300">
                Free consultation
              </button>
            </Reveal>
          </div>

          {/* Right: frosted capability panel */}
          <Reveal delay={300} className="w-full max-w-md">
            <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md px-5 sm:px-6">
              {capabilities.map((cap, i) => (
                <Reveal key={cap.index} delay={300 + i * 110}>
                  <div className={`flex gap-5 py-5 ${i < capabilities.length - 1 ? 'border-b border-white/15' : ''}`}>
                    <span className="font-mono text-[11px] tracking-[0.15em] text-white/55 mt-1 flex-shrink-0">
                      {cap.index}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base sm:text-lg font-medium text-white">{cap.title}</h3>
                        <ChevronRight
                          className="w-4 h-4 text-white/40 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-white"
                        />
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/70">{cap.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}